import React from 'react';
import axios from 'axios';
import { isNil } from 'lodash';

import { type AsyncExchangeState, useAsyncChatExchange } from '~/components/AiChat/hooks/useAsyncChatExchange';
import { type OrganizedChatUpdates, useChatRealTimeUpdates } from '~/components/AiChat/hooks/useChatRealTimeUpdates';
import type { PredefinedPromptItem } from '~/components/AiChat/hooks/usePredefinedPrompts';
import { PredefinedPromptsProvider, usePredefinedPromptsContext } from '~/components/AiChat/hooks/usePredefinedPrompts';
import type {
  AiChatApiResponse,
  AiChatExchangeApiResponse,
  AiChatExchangesAndReferencesApiResponse,
  AiChatReferences,
  IssueTokenResponse,
  UpdateExchangeUserFeedbackCallback,
  WebpagePreviewData,
  WebpagePreviews,
} from '~/components/AiChat/types';
import { extractWebUrlsFromMarkdown } from '~/components/AiChat/utils/urlExtractor';
import { useClaim } from '~/components/ClaimContainer';
import { useCliveClaimContext } from '~/components/SystemConfiguration/CliveConfiguration/CliveClaimContext';
import useDataFetcher from '~/components/useDataFetcher';
import type { WebpagePreviewResponse } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

export interface UseAiChatReturn {
  chat: AiChatApiResponse | null;
  reloadChat: () => Promise<AiChatApiResponse | null>;
  isChatLoading: boolean;
  isChatError: boolean;
  getChatResponse: (userInput: string, contextDocumentIds?: number[], promptKey?: string) => Promise<void>;
  updateExchangeUserFeedback: UpdateExchangeUserFeedbackCallback;
  restartChat: () => Promise<void>;
  renameChat: (newTitle: string) => Promise<void>;
  createNewChat: () => Promise<AiChatApiResponse | undefined>;
  loadChatById: (chatId: number) => Promise<AiChatApiResponse | undefined>;
  regenerateResponse: (exchangeId: number) => Promise<void>;
  isFetchingResponse: boolean;
  activeAsyncExchange: AsyncExchangeState | null;
  retryExchange: () => Promise<void>;
  exchanges: AiChatExchangeApiResponse[];
  references?: AiChatReferences;
  chatDocumentIds?: number[];
  setChatDocumentIds?: React.Dispatch<React.SetStateAction<number[]>>;
  isAiChatOpen: boolean;
  setIsAiChatOpen: React.Dispatch<React.SetStateAction<boolean>>;
  predefinedPrompts?: Record<string, PredefinedPromptItem>;
  // Real-time updates
  organizedRealTimeUpdates: OrganizedChatUpdates;
  realTimeConnected?: boolean;
  // Webpage previews
  webpagePreviews: WebpagePreviews;
  getWebpagePreview: (url: string) => WebpagePreviewData | undefined;
  isWebpagePreviewLoading: (url: string) => boolean;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && !isNil(value);
const isExchangeList = (value: unknown): value is AiChatExchangeApiResponse[] => Array.isArray(value);
const isReferenceMap = (value: unknown): value is AiChatReferences => isRecord(value);

const withChatDefaults = <T extends AiChatExchangesAndReferencesApiResponse>(payload: T): T => {
  const fields: Partial<T> = isRecord(payload) ? payload : {};
  return {
    ...fields,
    exchanges: isExchangeList(fields.exchanges) ? fields.exchanges : [],
    references: isReferenceMap(fields.references) ? fields.references : {},
  } as T;
};

export const useChat = (): UseAiChatReturn => {
  const { claim } = useClaim({ claimRequired: true });
  const [isAiChatOpen, setIsAiChatOpen] = React.useState(false);
  const [chatDocumentIds, setChatDocumentIds] = React.useState([] as number[]);
  const baseRoute = `/api/v1/claims/${claim.id}/chat`;

  const { isClaimChatEnabled } = useCliveClaimContext();
  const { prompts } = usePredefinedPromptsContext();
  const { data, reloadData, isLoading, isError } = useDataFetcher<AiChatApiResponse>(
    baseRoute,
    {},
    { shouldFetch: isClaimChatEnabled, shouldReportAxiosError: false }
  );
  // Memoized on `data` so the fetcher's payload keeps a stable identity for the effect below.
  const fetchedChat = React.useMemo(() => (data ? withChatDefaults(data) : undefined), [data]);

  // Chat state
  const [currentChat, setCurrentChat] = React.useState<AiChatApiResponse | null>(null);
  const [exchanges, setExchanges] = React.useState<AiChatExchangeApiResponse[]>([]);
  const [references, setReferences] = React.useState<AiChatReferences>({});
  const [issueTokenResponse, setIssueTokenResponse] = React.useState<IssueTokenResponse | null>(null);

  // Loading states
  const [isRestarting, setIsRestarting] = React.useState(false);

  // Webpage previews
  const [webpagePreviews, setWebpagePreviews] = React.useState<WebpagePreviews>({});
  const [loadingWebpageUrls, setLoadingWebpageUrls] = React.useState<Set<string>>(new Set());

  const webpagePreviewsRef = React.useRef<WebpagePreviews>(webpagePreviews);
  const loadingWebpageUrlsRef = React.useRef<Set<string>>(loadingWebpageUrls);
  const exchangesRef = React.useRef(exchanges);
  exchangesRef.current = exchanges;

  React.useEffect(() => {
    webpagePreviewsRef.current = webpagePreviews;
    loadingWebpageUrlsRef.current = loadingWebpageUrls;
  }, [webpagePreviews, loadingWebpageUrls]);

  // Takes a payload already through `withChatDefaults` — every caller normalizes at its own fetch.
  const updateChatState = React.useCallback((chatData: AiChatApiResponse) => {
    setCurrentChat(chatData);
    setExchanges(chatData.exchanges);
    setReferences(chatData.references);
    setIssueTokenResponse(chatData.issue_token_response ?? null);
  }, []);

  const handleReloadData = React.useCallback(async () => {
    let chatResponse: AiChatApiResponse | undefined;

    if (currentChat?.internal_id) {
      try {
        const { data } = await axios.get<AiChatApiResponse>(`${baseRoute}/${currentChat.internal_id}`);
        chatResponse = withChatDefaults(data);
      } catch (error) {
        await reportAxiosError(error);
        return null;
      }
    } else {
      // The mount fetch is already issuing this very GET, and GET /chat is a get-or-create: a
      // second one alongside it auto-creates a second chat, and whichever response lands last
      // re-points `currentChat` and the chat-scoped real-time token — stranding the answer to a
      // question submitted against the other one. Wait for the fetch in flight instead; its payload
      // reaches this hook through `fetchedChat` either way.
      if (isLoading) return null;
      const reloaded = await reloadData();
      chatResponse = reloaded && withChatDefaults(reloaded);
    }

    if (!chatResponse) return null;
    updateChatState(chatResponse);
    return chatResponse;
  }, [currentChat?.internal_id, baseRoute, isLoading, reloadData, updateChatState]);

  const isTokenCloseToExpiring = React.useCallback((expiresAt: string | undefined): boolean => {
    if (!expiresAt) return true;
    const fiveMinutesInMs = 5 * 60 * 1000;
    return new Date(expiresAt).getTime() - Date.now() < fiveMinutesInMs;
  }, []);

  // Real-time subscription (already live off issueTokenResponse) — declared here so the async
  // exchange hook below can watch its events for the `done` signal.
  const { organizedUpdates: organizedRealTimeUpdates, isConnected: realTimeConnected } = useChatRealTimeUpdates({
    issueTokenResponse,
    exchanges,
    enabled: !!issueTokenResponse && isClaimChatEnabled,
  });

  // A completed async exchange resolved (via `done` event or poll): render its answer, then refresh
  // references from the chat (the single-exchange GET carries none; learn-on-read cached them
  // server-side). We refresh *only* references — a full chat reload replaces `exchanges` wholesale, so
  // a still-stale read (the just-resolved exchange not yet persisted with its answer) would drop the
  // answer and let the recovery effect re-attach and poll again.
  const handleResolvedExchange = React.useCallback(
    async (exchange: AiChatExchangeApiResponse) => {
      setExchanges((prev) => {
        const exists = prev.some((e) => String(e.id) === String(exchange.id));
        const next = exists
          ? prev.map((e) => (String(e.id) === String(exchange.id) ? exchange : e))
          : [...prev, exchange];
        // Drop any *other* still-unanswered rows. A retry (or a fresh question after a failure)
        // submits a new server exchange id, orphaning the original in-progress row — it never gets
        // an answer, so without this it lingers as a duplicated turn stuck on a perpetual loader.
        return next.filter((e) => String(e.id) === String(exchange.id) || e.response || e.response_time);
      });
      if (!currentChat?.internal_id) return;
      try {
        const { data } = await axios.get<AiChatApiResponse>(`${baseRoute}/${currentChat.internal_id}`);
        setReferences(withChatDefaults(data).references);
      } catch (error) {
        await reportAxiosError(error);
      }
    },
    [currentChat?.internal_id, baseRoute]
  );

  // Adopt a fresh token from submit/retry only when there's no live one or the current is near
  // expiry. The subscription is chat-scoped, so a new exchange rides the existing connection —
  // re-pointing it every message would tear it down and blank the live agent-progress view until the
  // next snapshot.
  const handleTokenRefresh = React.useCallback(
    (token: IssueTokenResponse) =>
      setIssueTokenResponse((prev) => (!prev || isTokenCloseToExpiring(prev.expires_at) ? token : prev)),
    [isTokenCloseToExpiring]
  );

  const {
    activeAsyncExchange,
    isFetching: isFetchingAsyncResponse,
    submitAsync,
    submitAsyncRegenerate,
    retryExchange,
  } = useAsyncChatExchange({
    baseRoute,
    chatInternalId: currentChat?.internal_id ?? null,
    organizedRealTimeUpdates,
    realTimeConnected,
    exchanges,
    onResolved: handleResolvedExchange,
    onTokenRefresh: handleTokenRefresh,
  });

  const getChatResponse = React.useCallback(
    async (userInput: string, contextDocumentIds?: number[], promptKey?: string) => {
      await submitAsync(userInput, contextDocumentIds ?? chatDocumentIds, promptKey);
    },
    [chatDocumentIds, submitAsync]
  );

  const updateExchangeUserFeedback = React.useCallback(
    async (exchangeId: number, feedbackScore: number, feedbackText: string) => {
      try {
        await axios.patch(`${baseRoute}/exchanges/${exchangeId}`, {
          user_feedback_score: feedbackScore,
          user_feedback_text: feedbackText,
          chat_id: currentChat?.internal_id,
        });
        await handleReloadData();
        return true;
      } catch (error) {
        await reportAxiosError(error);
        return false;
      }
    },
    [baseRoute, currentChat?.internal_id, handleReloadData]
  );

  const createNewChat = React.useCallback(async () => {
    const previousChat = currentChat;
    const previousExchanges = exchanges;

    setCurrentChat(null);
    setExchanges([]);

    try {
      setIsRestarting(true);
      const { data } = await axios.post<AiChatApiResponse>(`${baseRoute}/new`);
      const chatData = withChatDefaults(data);
      updateChatState(chatData);
      return chatData;
    } catch (error) {
      setCurrentChat(previousChat);
      setExchanges(previousExchanges);
      await reportAxiosError(error);
      throw error;
    } finally {
      setIsRestarting(false);
    }
  }, [baseRoute, updateChatState, currentChat, exchanges]);

  const restartChat = React.useCallback(async () => {
    await createNewChat();
  }, [createNewChat]);

  const renameChat = React.useCallback(
    async (newTitle: string) => {
      if (!currentChat?.internal_id) return;

      const chatId = currentChat.internal_id;
      const previousTitle = currentChat.chat_title;
      setCurrentChat((prev) => (prev?.internal_id === chatId ? { ...prev, chat_title: newTitle } : prev));

      try {
        await axios.patch(`${baseRoute}/${chatId}/rename`, {
          title: newTitle,
        });
      } catch (error) {
        setCurrentChat((prev) => (prev?.internal_id === chatId ? { ...prev, chat_title: previousTitle } : prev));
        await reportAxiosError(error);
        throw error;
      }
    },
    [baseRoute, currentChat?.internal_id, currentChat?.chat_title]
  );

  const loadChatById = React.useCallback(
    async (chatId: number) => {
      const previousChat = currentChat;
      const previousExchanges = exchanges;

      setCurrentChat(null);
      setExchanges([]);

      try {
        setIsRestarting(true);
        const { data } = await axios.get<AiChatApiResponse>(`${baseRoute}/${chatId}`);
        const chatData = withChatDefaults(data);
        updateChatState(chatData);
        return chatData;
      } catch (error) {
        setCurrentChat(previousChat);
        setExchanges(previousExchanges);
        await reportAxiosError(error);
        throw error;
      } finally {
        setIsRestarting(false);
      }
    },
    [baseRoute, updateChatState, currentChat, exchanges]
  );

  const regenerateResponse = React.useCallback(
    async (exchangeId: number) => {
      const target = exchangesRef.current.find((exchange) => String(exchange.id) === String(exchangeId));
      if (!target) return;
      const previousExchanges = exchangesRef.current;
      // Drop the regenerated turn so the resolved answer won't render beside the stale one; restore
      // it if the submit fails, since the exchange still exists on the backend.
      setExchanges((prev) => prev.filter((exchange) => String(exchange.id) !== String(exchangeId)));
      try {
        await submitAsyncRegenerate(String(exchangeId), target.user_input, target.context_document_ids);
      } catch {
        setExchanges(previousExchanges);
      }
    },
    [submitAsyncRegenerate]
  );

  const fetchWebpagePreviews = React.useCallback(
    async (urls: string[]) => {
      if (!urls.length) return;

      const uncachedUrls = urls.filter(
        (url) => !webpagePreviewsRef.current[url] && !loadingWebpageUrlsRef.current.has(url)
      );
      if (!uncachedUrls.length) return;

      setLoadingWebpageUrls((prev) => new Set([...prev, ...uncachedUrls]));

      try {
        const fetchPromises = uncachedUrls.map(async (url) => {
          try {
            const { data } = await axios.post<WebpagePreviewResponse>(`${baseRoute}/webpage_preview`, { url });
            return { url, data: data.preview };
          } catch {
            return {
              url,
              data: { url, title: null, description: null, image_url: null, error: 'Failed to fetch preview' },
            };
          }
        });

        const results = await Promise.all(fetchPromises);
        const newPreviews = Object.fromEntries(results.map((r) => [r.url, r.data as WebpagePreviewData]));
        setWebpagePreviews((prev) => ({ ...prev, ...newPreviews }));
      } finally {
        setLoadingWebpageUrls((prev) => {
          const next = new Set(prev);
          uncachedUrls.forEach((url) => next.delete(url));
          return next;
        });
      }
    },
    [baseRoute]
  );

  const getWebpagePreview = React.useCallback(
    (url: string): WebpagePreviewData | undefined => {
      return webpagePreviews[url];
    },
    [webpagePreviews]
  );

  const isWebpagePreviewLoading = React.useCallback(
    (url: string): boolean => {
      return loadingWebpageUrls.has(url);
    },
    [loadingWebpageUrls]
  );

  React.useEffect(() => {
    if (!fetchedChat) return;
    setCurrentChat(fetchedChat);
    setIssueTokenResponse(fetchedChat.issue_token_response ?? null);
    if (!exchanges?.length) {
      setExchanges(fetchedChat.exchanges);
      setReferences(fetchedChat.references);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedChat]);

  // Extract URLs from exchanges and fetch webpage previews
  React.useEffect(() => {
    if (!exchanges.length) return;
    const allUrls = exchanges.flatMap((e) => (e.response ? extractWebUrlsFromMarkdown(e.response) : []));
    if (allUrls.length) {
      fetchWebpagePreviews(allUrls);
    }
  }, [exchanges, fetchWebpagePreviews]);

  return React.useMemo<UseAiChatReturn>(
    () => ({
      chat: currentChat,
      reloadChat: handleReloadData,
      isChatLoading: isLoading || isRestarting,
      isChatError: isError as boolean,
      getChatResponse,
      isFetchingResponse: isFetchingAsyncResponse,
      activeAsyncExchange,
      retryExchange,
      exchanges,
      references,
      updateExchangeUserFeedback,
      restartChat,
      renameChat,
      createNewChat,
      loadChatById,
      regenerateResponse,
      chatDocumentIds,
      setChatDocumentIds,
      isAiChatOpen,
      setIsAiChatOpen,
      predefinedPrompts: prompts,
      organizedRealTimeUpdates,
      realTimeConnected,
      webpagePreviews,
      getWebpagePreview,
      isWebpagePreviewLoading,
    }),
    [
      currentChat,
      handleReloadData,
      isLoading,
      isRestarting,
      isError,
      getChatResponse,
      isFetchingAsyncResponse,
      activeAsyncExchange,
      retryExchange,
      exchanges,
      references,
      updateExchangeUserFeedback,
      restartChat,
      renameChat,
      createNewChat,
      loadChatById,
      regenerateResponse,
      chatDocumentIds,
      setChatDocumentIds,
      isAiChatOpen,
      setIsAiChatOpen,
      prompts,
      organizedRealTimeUpdates,
      realTimeConnected,
      webpagePreviews,
      getWebpagePreview,
      isWebpagePreviewLoading,
    ]
  );
};

export const ChatContext = React.createContext<UseAiChatReturn>({
  chat: null,
  reloadChat: () => Promise.resolve(null),
  isChatLoading: true,
  isChatError: false,
  getChatResponse: () => Promise.resolve(),
  isFetchingResponse: false,
  activeAsyncExchange: null,
  retryExchange: () => Promise.resolve(),
  exchanges: [],
  references: undefined,
  updateExchangeUserFeedback: () => Promise.resolve(false),
  restartChat: () => Promise.resolve(),
  renameChat: () => Promise.resolve(),
  createNewChat: () => Promise.resolve(undefined),
  loadChatById: () => Promise.resolve(undefined),
  regenerateResponse: () => Promise.resolve(),
  chatDocumentIds: [],
  setChatDocumentIds: () => undefined,
  isAiChatOpen: false,
  setIsAiChatOpen: () => false,
  predefinedPrompts: {},
  organizedRealTimeUpdates: { byExchangeId: {}, pending: [] },
  webpagePreviews: {},
  getWebpagePreview: () => undefined,
  isWebpagePreviewLoading: () => false,
});

export const ChatProvider: React.FC<React.PropsWithChildren> = ({ children }) => (
  <PredefinedPromptsProvider>
    <ChatContextInner>{children}</ChatContextInner>
  </PredefinedPromptsProvider>
);

const ChatContextInner: React.FC<React.PropsWithChildren> = ({ children }) => {
  const value = useChat();
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChatContext = (): UseAiChatReturn => {
  return React.useContext(ChatContext);
};
